// Сквозные отчёты: что происходит МЕЖДУ источниками.
//
// Здесь только формы ответа ядра и правила ПОКАЗА — разметки нет, она в
// pages/CrossReports.tsx. Расчётов здесь тоже нет: всё, что считается, считает ядро
// (xreports_api.py). Причина та же, по которой lib/payments.ts — единственное место,
// где считается долг: разъедись формула на два места, и «сколько денег висит» стало бы
// зависеть от того, на каком экране смотришь.
//
// Отдельно про то, чего этот раздел НЕ считает:
//   • расхождение состава сделки с суммой — это products_api и его классификатор причин;
//   • дебиторку и пробные записи в ней — это lib/payments.ts.
// Ни то, ни другое здесь не пересчитывается и не дублируется.

import { fmtDate, fmtMoney } from './format'

/** Форма страницы, общая для всех модулей ядра (см. _выборка в xreports_api.py). */
export interface Список<T> {
  rows: T[]
  total: number
  next: number | null
  limit: number
  start: number
}

/** Границы одного источника: с какого дня он вообще что-то знает. */
export interface Граница {
  src: string
  label: string
  first_day: string | null
  last_day: string | null
  rows: number | null
  /** На сколько дней последний день источника отстаёт от сегодня. Есть только у банка. */
  lag_days: number | null
}

/**
 * Окно, в котором отчёт вправе судить: пересечение окон его источников.
 *
 * `unknown` не пуст — значит про какой-то источник ядро не знает ничего, и окна нет
 * ЦЕЛИКОМ. `disjoint` — окна источников не пересекаются вовсе. В обоих случаях
 * `from`/`to` пусты, и это РАЗНОЕ от «ноль записей»: экран обязан сказать «судить не о
 * чем», а не показать пустую таблицу.
 */
export interface Окно {
  from: string | null
  to: string | null
  sources: string[]
  unknown?: string[]
  disjoint?: boolean
}

/** Паспорт отчёта: он приезжает из ядра, а не пишется на экране. */
export interface Паспорт {
  code: string
  path: string
  title: string
  question: string
  why: string
  sources: string[]
  blind: string
  window: Окно
}

export interface СправочникиXR {
  reports: Паспорт[]
  bounds: Record<string, Граница>
  freshness: { entity: string; last_sync_at: string | null; last_count: number | null; age_min: number | null }[]
  oldest: { entity: string; last_sync_at: string | null } | null
  hang_days: number
  page: number
  data_notes: Record<string, string>
}

/** Общий хвост любого отчёта. */
export interface ХвостОтчёта {
  report: string
  bounds: Record<string, Граница>
  window: Окно
  hang_days?: number
  data_notes: Record<string, string>
  totals: Record<string, number | string | null>
}

// ─────────────────────────── строки отчётов ───────────────────────────

/** О1. Приход в банке без разноски и счёт, которым он опознаётся. */
export interface СтрокаБанк {
  src_id: number
  op_date: string | null
  amount: number | null
  cp_name: string | null
  cp_inn: string | null
  purpose: string | null
  refs: string | null
  docnum: string | null
  why: string | null
  bank: string | null
  match: 'one' | 'many' | 'none'
  n_cand: number
  has_ref: boolean
  inv_id: number | null
  inv_title: string | null
  inv_stage: string | null
  inv_sum: number | null
  deal_id: number | null
  deal_title: string | null
}

/** О2. Поставщику заплачено, закрывающих нет. */
export interface СтрокаПоставщик {
  bitrix_id: number
  opportunity: number | null
  currency: string | null
  supplier_doc_no: string | null
  what_for: string | null
  paid_on: string | null
  days: number | null
  planned_on: string | null
  supplier: string | null
  deal_id: number | null
  deal_title: string | null
}

/** О2б. Заказ поставщику есть, прихода нет. */
export interface СтрокаЗакупка {
  ref_key: string
  number: string | null
  doc_date: string | null
  sum_doc: number | null
  currency_ref: string | null
  status: string | null
  supplier_doc_no: string | null
  wanted_date: string | null
  days: number | null
  comment: string | null
  deal_id: number | null
  deal_title: string | null
}

/** О3. Работа принята, счёта клиенту нет. */
export interface СтрокаРаботы {
  deal_id: number
  deal_title: string | null
  opportunity: number | null
  currency: string | null
  stage_id: string | null
  n_verif: number
  n_report: number
  accepted_at: string | null
  days: number | null
  n_inv: number
  n_paid: number
  n_avr: number
  state: 'no_invoice' | 'unpaid' | 'paid'
}

/** О4. Деньги пришли, закрыть нечем. */
export interface СтрокаДеньги {
  deal_id: number
  deal_title: string | null
  opportunity: number | null
  currency: string | null
  stage_id: string | null
  closed: boolean
  got: number | null
  n_ops: number
  first_pay: string | null
  last_pay: string | null
  days: number | null
}

// ─────────────────────────── правила показа ───────────────────────────

/**
 * Подпись состояния матча. Слово «кандидат» здесь обязательно: разносит платёж
 * бухгалтер, а отчёт только показывает, где искать. «Найден счёт» читалось бы как
 * решение, принятое за человека.
 */
export const ПОДПИСЬ_МАТЧА: Record<СтрокаБанк['match'], string> = {
  one: 'кандидат один',
  many: 'кандидатов несколько',
  none: 'кандидата нет',
}

/**
 * Почему кандидата нет — два РАЗНЫХ ответа, и путать их нельзя.
 * Пустой `refs` значит, что портал не нашёл номера в назначении платежа: искать было
 * не по чему. Это не то же самое, что «искали и не нашли».
 */
export const почемуНетКандидата = (с: СтрокаБанк): string =>
  с.has_ref
    ? 'номер в назначении есть, счёта с таким номером и такой суммой не нашлось'
    : 'в назначении платежа нет номера — искать было не по чему'

export const ПОДПИСЬ_СОСТОЯНИЯ: Record<СтрокаРаботы['state'], string> = {
  no_invoice: 'счёта нет',
  unpaid: 'счёт есть, не оплачен',
  paid: 'счёт оплачен',
}

/**
 * Одна строка «источник — с какого дня». Экран обязан её показать: без неё цифра
 * отчёта не привязана ни к чему, и «нарушений 106» читается как «за всё время».
 */
export const подписьГраницы = (г: Граница): string => {
  const с = г.first_day ? fmtDate(г.first_day) : '—'
  const по = г.last_day ? fmtDate(г.last_day) : '—'
  const отставание = г.lag_days && г.lag_days > 0 ? `, отстаёт на ${г.lag_days} дн.` : ''
  return `${г.label}: ${с} — ${по}${отставание}`
}

/**
 * Подпись окна отчёта. Три исхода, а не два: окно есть; про источник ничего не известно;
 * окна источников не пересекаются. Последние два — не «ничего не найдено».
 */
export const подписьОкна = (о: Окно | undefined): string => {
  if (!о) return ''
  if (о.unknown && о.unknown.length > 0) {
    return 'Судить не о чем: ядро не знает границ источника — ' + о.unknown.join(', ')
  }
  if (о.disjoint) return 'Судить не о чем: окна источников не пересекаются'
  if (!о.from || !о.to) return 'Окно отчёта не определено'
  return `Отчёт судит только за ${fmtDate(о.from)} — ${fmtDate(о.to)}`
}

/** Есть ли вообще окно. Пусто — таблицу показывать нельзя, нужен текст. */
export const окноЕсть = (о: Окно | undefined): boolean => Boolean(о && о.from && о.to)

/** Деньги в рублях. Отдельная функция, чтобы формат был один на весь раздел. */
export const рубли = (v: number | string | null | undefined): string =>
  v === null || v === undefined ? '—' : fmtMoney(v, 'RUB')

/**
 * Сумма 1С. Валюта приезжает ссылкой на справочник, которого в ядре нет, поэтому
 * знака валюты здесь не будет — и это честнее, чем нарисовать рубль наугад.
 */
export const суммаБезВалюты = (v: number | null | undefined): string =>
  v === null || v === undefined ? '—' : v.toLocaleString('ru-RU', { maximumFractionDigits: 2 })

export const дни = (n: number | null | undefined): string =>
  n === null || n === undefined ? '—' : `${n} дн.`
